export const CALCULATOR_CATEGORIES = [
  {
    id: "machining",
    name: "Machining",
    icon: "MFG",
    accent: "cyan",
    description: "Cutting data, CNC code, tooling, tap drills, bore paths, and shop setup math.",
  },
  {
    id: "cnc-gcode",
    name: "CNC / G-Code",
    icon: "CNC",
    accent: "purple",
    description: "G-code setup, coordinate math, interpolation, offsets, safe starts, and CNC helper tools.",
  },
  {
    id: "threads",
    name: "Threads & Tapping",
    icon: "THD",
    accent: "cyan",
    description: "Tap drills, thread pitch, engagement, pipe threads, percentage of thread, and thread cutting math.",
  },
  {
    id: "holemaking",
    name: "Drilling & Holemaking",
    icon: "DRL",
    accent: "blue",
    description: "Drill depth, clearance holes, reaming, counterbores, countersinks, spotting, and bore planning.",
  },
  {
    id: "inspection",
    name: "Metrology & Inspection",
    icon: "QA",
    accent: "green",
    description: "GD&T, true position, tolerance stack-up, limit checks, capability, sine bars, and thermal compensation.",
  },
  {
    id: "welding",
    name: "Welding",
    icon: "WLD",
    accent: "red",
    description: "Heat input, filler usage, weld time, and weld cost estimating.",
  },
  {
    id: "sheet-metal",
    name: "Sheet Metal & Fabrication",
    icon: "SM",
    accent: "yellow",
    description: "Bend allowance, bend deduction, flat patterns, K-factor, tonnage, sheet yield, and press force.",
  },
  {
    id: "mechanical-design",
    name: "Mechanical Design",
    icon: "MECH",
    accent: "orange",
    description: "Torque, horsepower, clamp load, bearings, springs, shafts, keys, and machine design helpers.",
  },
  {
    id: "power-transmission",
    name: "Gears / Pulleys / Power Transmission",
    icon: "PWR",
    accent: "pink",
    description: "Gear ratios, pitch diameters, module, diametral pitch, pulley RPM, belt speed, and sprocket ratios.",
  },
  {
    id: "shop-math",
    name: "Shop Math / Trig / Geometry",
    icon: "TRI",
    accent: "cyan",
    description: "Trig, circles, bolt patterns, tapers, chords, radii, angles, conversion, and layout geometry.",
  },
  {
    id: "materials",
    name: "Materials",
    icon: "MAT",
    accent: "red",
    description: "Material weight, density, hardness, thermal expansion, material cost, plate, bar, and machinability data.",
  },
  {
    id: "costing",
    name: "Costing / Estimating",
    icon: "QTE",
    accent: "purple",
    description: "Shop rates, machine time, material cost, tooling cost, part cost, batch pricing, margin, and quote helpers.",
  },
];

const tool = (id, name, categories, badge, description, tags = []) => ({
  id,
  name,
  categories,
  category: categories[0],
  badge,
  icon: badge,
  description,
  tags,
});

export const CALCULATOR_TOOLS = [
  tool("g-code", "G-code Starter", ["cnc-gcode", "machining"], "G01", "Generate starter G-code for CNC milling and turning workflows."),
  tool("feeds-speeds", "Feeds, Speeds & RPM", ["machining"], "RPM", "Calculate cutting speed, spindle speed, chip load, and feedrate."),
  tool("gear-ratio", "Gear Ratio / Gear Cutting", ["power-transmission", "machining"], "GR", "Calculate gear ratios and shop gear cutting values."),
  tool("tool-deflection", "Tool Deflection", ["machining"], "TD", "Estimate tool deflection from cutter geometry and cutting load."),
  tool("tool-wear", "Tool Wear", ["machining"], "TW", "Track tool wear estimates by operation and saved tooling."),
  tool("tap-drill", "Tap Drill", ["threads", "holemaking", "machining"], "TAP", "Find tap drill sizes and thread engagement values."),
  tool("threading", "Thread Pitch", ["threads", "machining", "inspection"], "THD", "Calculate thread pitch and thread measurement values."),
  tool("hole-sizing", "Hole Clearance", ["holemaking", "inspection", "threads"], "HOLE", "Calculate clearance, close fit, and standard hole sizing guidance."),
  tool("trig", "Trig Calculator", ["shop-math", "machining"], "TRI", "Solve right-triangle and shop trigonometry problems."),
  tool("bolt-pattern", "Bolt Pattern", ["cnc-gcode", "shop-math", "machining"], "BP", "Calculate bolt circle coordinates and layout values."),
  tool("tool-cost-per-inch", "Tool Cost Per Inch Cut", ["costing", "machining"], "$/IN", "Estimate tool cost based on tool life and length of cut."),
  tool("taper", "Taper Calculator", ["cnc-gcode", "shop-math", "machining"], "TA", "Calculate taper angle, compound setting, and taper setup values."),
  tool("drill-depth", "Drill Depth", ["holemaking", "machining", "cnc-gcode"], "DRL", "Calculate drill tip allowance and programmed drill depth."),
  tool("counterbore", "Counterbore", ["holemaking", "machining"], "CB", "Estimate counterbore diameter and depth from fastener data."),
  tool("countersink", "Countersink", ["holemaking", "machining"], "CSK", "Calculate countersink depth from top diameter and included angle."),
  tool("bore-interpolation", "Bore Interpolation", ["cnc-gcode", "holemaking", "machining"], "BI", "Calculate interpolated bore toolpath diameter and radius."),
  tool("horsepower", "Horsepower", ["machining", "mechanical-design"], "HP", "Estimate cutting horsepower from material removal rate."),
  tool("gdt", "GD&T Helper", ["inspection"], "GD&T", "Review GD&T symbols and apply tolerance concepts to real dimensions."),
  tool("true-position", "True Position", ["inspection"], "POS", "Calculate diametrical true position from actual and nominal coordinates."),
  tool("shop-math", "Shop Calculator", ["shop-math"], "CALC", "Run general shop arithmetic and calculator operations."),
  tool("unit-conversion", "Unit Conversion", ["shop-math", "materials", "inspection"], "UNIT", "Convert common shop units across inch, metric, angle, and weight values."),
  tool("bend-allowance", "Bend Allowance", ["sheet-metal"], "BA", "Calculate developed bend allowance from radius, angle, thickness, and K-factor."),
  tool("k-factor", "K-Factor", ["sheet-metal"], "K", "Back-calculate K-factor from measured bend allowance and bend geometry."),
  tool("press-brake-tonnage", "Press Brake Tonnage", ["sheet-metal"], "TON", "Estimate air-bending tonnage from thickness, tensile strength, bend length, and V-die opening."),
  tool("kerf-compensation", "Kerf Compensation", ["cnc-gcode", "sheet-metal"], "KERF", "Calculate kerf offset for outside, inside, and centerline cutting paths."),
  tool("weld-heat-input", "Weld Heat Input", ["welding"], "HI", "Estimate weld heat input from voltage, amperage, travel speed, and efficiency."),
  tool("weld-cost", "Weld Cost", ["welding", "costing"], "W$", "Estimate filler usage, weld time, labor, consumables, and total weld cost."),
  tool("print-cost", "3D Print Cost", ["costing", "materials"], "3D", "Estimate filament, electricity, labor, failure-adjusted cost, and suggested price."),
  tool("casting-shrinkage", "Casting Shrinkage", ["materials", "mechanical-design"], "CAST", "Estimate pattern dimension and shrink allowance from approximate alloy shrinkage."),
  tool("job-quote", "Job Quote", ["costing"], "QUOTE", "Estimate manufacturing cost, break-even, suggested quote, and profit."),
  tool("tolerance-stack", "Tolerance Stack-Up", ["inspection", "shop-math"], "STACK", "Calculate worst-case and RSS tolerance stack limits for assemblies and features."),
  tool("process-capability", "Process Capability", ["inspection"], "CPK", "Calculate Cp and Cpk from specification limits, mean, and standard deviation."),
  tool("oee", "OEE", ["costing"], "OEE", "Calculate availability, performance, quality, and overall equipment effectiveness."),
  tool("takt-capacity", "Takt & Capacity", ["costing"], "TAKT", "Estimate takt time, effective cycle time, capacity, and required resources."),
  tool("thermal-expansion", "Thermal Expansion", ["materials", "inspection"], "TEMP", "Estimate dimensional change from material coefficient and temperature change."),
  tool("sheet-yield", "Sheet Yield", ["sheet-metal", "cnc-gcode", "costing"], "YLD", "Estimate rectangular blank yield, sheet utilization, and scrap percentage."),
  tool("hydraulic-force", "Hydraulic / Press Force", ["sheet-metal", "mechanical-design"], "FORCE", "Calculate cylinder force and approximate tonnage from pressure and bore size."),
  tool("inventory-reorder", "Inventory Reorder Point", ["costing"], "ROP", "Estimate reorder point from usage, lead time, safety stock, and order quantity."),
  tool("surface-finish", "Surface Finish Estimate", ["machining"], "RA", "Estimate theoretical turning surface finish from feed per revolution and nose radius."),
  tool("stepover-scallop", "Step-over / Scallop Height", ["machining", "cnc-gcode"], "SCAL", "Estimate ball end mill scallop height from cutter radius and stepover."),
  tool("feed-chipload", "Feed Rate from Chip Load", ["machining"], "IPM", "Calculate feedrate from RPM, flutes, and chip load per tooth."),
  tool("mrr", "Material Removal Rate", ["machining"], "MRR", "Calculate material removal rate from width, depth, and feedrate."),
  tool("arc-center", "Arc Center I/J Calculator", ["cnc-gcode", "shop-math"], "IJK", "Calculate I and J arc center offsets from start and center coordinates."),
  tool("work-offset-shift", "Work Offset Shift", ["cnc-gcode", "inspection"], "G54", "Calculate corrected work offset values from measured part error."),
  tool("safe-start-block", "Safe Start Block Generator", ["cnc-gcode"], "SAFE", "Build a conservative starter modal block for common mill or lathe controls."),
  tool("thread-percentage", "Thread Percentage Calculator", ["threads"], "%TH", "Estimate percent of thread for 60-degree internal threads."),
  tool("thread-engagement", "Thread Engagement", ["threads", "mechanical-design"], "ENG", "Calculate length of engagement in diameters and engaged turns."),
  tool("thread-cut-depth", "Thread Cutting Depth", ["threads", "machining"], "DEP", "Calculate approximate single-point thread cutting depth."),
  tool("reamer-allowance", "Reamer Allowance", ["holemaking"], "REAM", "Recommend drill undersize allowance before reaming."),
  tool("spot-drill-diameter", "Spot Drill Diameter", ["holemaking"], "SPOT", "Calculate spot/chamfer top diameter from angle and depth."),
  tool("annular-cutter-speed", "Annular Cutter Speed", ["holemaking", "machining"], "ANN", "Calculate annular cutter RPM from diameter and cutting speed."),
  tool("go-nogo-limits", "Go / No-Go Limits", ["inspection"], "LIM", "Calculate upper and lower inspection limits from nominal and tolerances."),
  tool("mmc-bonus", "MMC / LMC Bonus Tolerance", ["inspection"], "MMC", "Calculate bonus tolerance from feature size departure from MMC."),
  tool("sine-bar", "Sine Bar Calculator", ["inspection", "shop-math"], "SIN", "Calculate gauge block height for a sine bar angle."),
  tool("bend-deduction", "Bend Deduction", ["sheet-metal"], "BD", "Calculate bend deduction from outside setback and bend allowance."),
  tool("flat-pattern", "Flat Pattern Length", ["sheet-metal"], "FLAT", "Calculate simple two-leg flat length from bend deduction."),
  tool("weld-volume", "Weld Volume", ["welding"], "VOL", "Estimate fillet weld volume from leg size and weld length."),
  tool("weld-travel-speed", "Weld Travel Speed", ["welding"], "WTS", "Calculate welding travel speed from weld length and arc time."),
  tool("gas-flow-converter", "Gas Flow Converter", ["welding"], "GAS", "Convert shielding gas flow between CFH and L/min."),
  tool("weld-filler-estimate", "Filler Metal Estimate", ["welding"], "FILL", "Estimate filler metal required from weld volume, density, and deposition efficiency."),
  tool("fillet-weld-throat", "Fillet Weld Throat Helper", ["welding"], "THRT", "Calculate theoretical throat from equal-leg fillet weld size."),
  tool("torque", "Torque Calculator", ["mechanical-design"], "TQ", "Calculate torque from force and lever arm."),
  tool("bolt-torque", "Bolt Torque Estimate", ["mechanical-design", "threads"], "BTQ", "Estimate tightening torque from clamp load and nut factor."),
  tool("bearing-life", "Bearing Life Basic L10", ["mechanical-design"], "L10", "Calculate basic L10 bearing life from dynamic load rating and equivalent load."),
  tool("spring-rate", "Compression Spring Rate", ["mechanical-design"], "SPR", "Calculate round-wire compression spring rate."),
  tool("module-gear", "Metric Module Gear", ["power-transmission"], "MOD", "Calculate metric gear pitch diameter and OD from module."),
  tool("pulley-rpm", "Pulley RPM", ["power-transmission", "mechanical-design"], "PLY", "Calculate driven pulley speed from driver RPM and pulley diameters."),
  tool("belt-speed", "Belt Speed", ["power-transmission", "mechanical-design"], "BELT", "Calculate belt surface speed from pulley diameter and RPM."),
  tool("chain-sprocket-ratio", "Chain / Sprocket Ratio", ["power-transmission"], "CHN", "Calculate driven sprocket speed and ratio from tooth counts."),
  tool("center-distance", "Pulley / Sprocket Center Distance", ["power-transmission"], "CTR", "Estimate center distance from pitch length and pulley diameters."),
  tool("circle-calculator", "Circle Calculator", ["shop-math"], "CIR", "Calculate circle area, circumference, radius, and diameter values."),
  tool("chord-height", "Chord Height / Sagitta", ["shop-math"], "CHD", "Calculate arc sagitta from radius and chord length."),
  tool("radius-from-chord", "Radius from Chord", ["shop-math"], "RAD", "Calculate radius from chord length and chord height."),
  tool("density-reference", "Density Reference", ["materials"], "DEN", "Look up common material densities used in shop estimating."),
  tool("hardness-conversion", "Hardness Conversion Reference", ["materials", "inspection"], "HRC", "Approximate Brinell to Rockwell conversion for shop reference."),
  tool("bar-stock-weight", "Bar Stock Weight", ["materials"], "BAR", "Estimate round bar weight from diameter, length, and material density."),
  tool("material-cost", "Material Cost Estimate", ["materials", "costing"], "M$", "Estimate material cost from weight, scrap, quantity, and cost per pound or kilogram."),
  tool("markup-margin", "Markup / Margin", ["costing"], "M%", "Convert cost to price using markup or margin."),
  tool("machine-time", "Machine Time", ["costing"], "TIME", "Estimate total machine hours from setup, cycle time, and quantity."),
  tool("tooling-cost", "Tooling Cost Calculator", ["costing"], "TC", "Estimate tooling cost per part and batch tooling burden."),
  tool("batch-pricing", "Batch Pricing", ["costing"], "BCH", "Estimate per-part batch price from fixed cost, variable cost, quantity, and margin."),
];

export const getCalculatorLabel = (toolId) =>
  CALCULATOR_TOOLS.find((calculator) => calculator.id === toolId)?.name ||
  toolId;

export const getCalculatorCategoryName = (categoryId) =>
  CALCULATOR_CATEGORIES.find((category) => category.id === categoryId)?.name ||
  categoryId;

export const getCalculatorCategory = (categoryId) =>
  CALCULATOR_CATEGORIES.find((category) => category.id === categoryId) ||
  CALCULATOR_CATEGORIES[0];
